import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link2, Copy, Download, Shield, Clock, User, Globe, RefreshCw, CheckCircle, XCircle, FileText, Ban } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentShareLinks() {
  const { condominiumId, documentId } = useParams();
  const navigate = useNavigate();
  const { token, selectedCondominium } = useAuth();
  
  const [document, setDocument] = useState(null);
  const [shareLinks, setShareLinks] = useState([]);
  const [downloadLogs, setDownloadLogs] = useState([]);
  const [logStats, setLogStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // Create form state
  const [ttlMinutes, setTtlMinutes] = useState(10);
  const [oneTime, setOneTime] = useState(false);
  const [scope, setScope] = useState('ADMIN_ONLY');

  const condoId = condominiumId || selectedCondominium?.condominium?.id;

  const fetchDocument = useCallback(async () => {
    if (!token || !condoId || !documentId) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setDocument(await res.json());
      }
    } catch (error) {
      console.error('Error fetching document:', error);
    }
  }, [token, condoId, documentId]);

  const fetchShareLinks = useCallback(async () => {
    if (!token || !condoId || !documentId) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/documents/${documentId}/share-links?include_expired=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setShareLinks(data.links || []);
      }
    } catch (error) {
      console.error('Error fetching share links:', error);
    }
  }, [token, condoId, documentId]);

  const fetchDownloadLogs = useCallback(async () => {
    if (!token || !condoId || !documentId) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/documents/${documentId}/download-log?limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setDownloadLogs(data.logs || []);
        setLogStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching download logs:', error);
    }
  }, [token, condoId, documentId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDocument(), fetchShareLinks(), fetchDownloadLogs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDocument, fetchShareLinks, fetchDownloadLogs]);

  const createShareLink = async () => {
    setCreating(true);
    
    try {
      const formData = new FormData();
      formData.append('ttl_minutes', ttlMinutes.toString());
      formData.append('one_time', oneTime.toString());
      formData.append('scope', scope);
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/documents/${documentId}/share-link`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Link creato con successo');
        
        // Copy to clipboard
        await navigator.clipboard.writeText(data.share_url);
        toast.success('Link copiato negli appunti');
        
        setCreateDialogOpen(false);
        fetchShareLinks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore creazione link');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setCreating(false);
    }
  };

  const revokeLink = async (linkToken) => {
    try {
      const res = await fetch(
        `${API_URL}/api/share/${linkToken}/revoke`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        toast.success('Link revocato');
        fetchShareLinks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore revoca');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const revokeAllLinks = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/documents/${documentId}/share-links/revoke-all`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Revocati ${data.revoked_count} link`);
        fetchShareLinks();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore revoca');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const copyLink = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success('Link copiato negli appunti');
  };

  const exportLog = async (format) => {
    try {
      const formData = new FormData();
      formData.append('format', format);
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/documents/${documentId}/download-log/export`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        const blob = await res.blob();
        const fileName = res.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') 
          || `download_log.${format.toLowerCase()}`;
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success(`Log esportato e archiviato in Documenti`);
      } else {
        toast.error('Errore esportazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Attivo</Badge>;
      case 'EXPIRED':
        return <Badge className="bg-gray-100 text-gray-800"><Clock className="h-3 w-3 mr-1" />Scaduto</Badge>;
      case 'REVOKED':
        return <Badge className="bg-red-100 text-red-800"><Ban className="h-3 w-3 mr-1" />Revocato</Badge>;
      case 'USED':
        return <Badge className="bg-blue-100 text-blue-800"><CheckCircle className="h-3 w-3 mr-1" />Utilizzato</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getResultBadge = (result) => {
    switch (result) {
      case 'ALLOWED':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />OK</Badge>;
      case 'DENIED':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Negato</Badge>;
      case 'EXPIRED':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Scaduto</Badge>;
      case 'REVOKED':
        return <Badge className="bg-red-100 text-red-800"><Ban className="h-3 w-3 mr-1" />Revocato</Badge>;
      default:
        return <Badge variant="outline">{result}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeLinksCount = shareLinks.filter(l => l.status === 'ACTIVE').length;

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/documents')}>
              Documenti
            </Button>
            <span>/</span>
            <span>Condivisione & Audit</span>
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            {document?.title || 'Documento'}
          </h1>
          <p className="text-muted-foreground">
            L-3 Compliance: Link temporanei, revoca e audit download
          </p>
        </div>
        <Button onClick={() => navigate('/documents')} variant="outline">
          Torna ai Documenti
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Link Attivi</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeLinksCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Download Totali</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{logStats?.total_downloads || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Con Watermark</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-cyan-600">{logStats?.watermarked || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Via Share Link</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{logStats?.via_share_link || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="links">
        <TabsList>
          <TabsTrigger value="links">
            <Link2 className="h-4 w-4 mr-2" />
            Link Condivisione ({shareLinks.length})
          </TabsTrigger>
          <TabsTrigger value="logs">
            <FileText className="h-4 w-4 mr-2" />
            Log Download ({downloadLogs.length})
          </TabsTrigger>
        </TabsList>

        {/* Share Links Tab */}
        <TabsContent value="links" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Link di Condivisione</CardTitle>
                <CardDescription>
                  Link temporanei con TTL e opzione monouso
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {activeLinksCount > 0 && (
                  <Button variant="outline" size="sm" onClick={revokeAllLinks}>
                    <Ban className="h-4 w-4 mr-2" />
                    Revoca Tutti
                  </Button>
                )}
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Link2 className="h-4 w-4 mr-2" />
                      Crea Link
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crea Link di Condivisione</DialogTitle>
                      <DialogDescription>
                        Crea un link temporaneo per condividere questo documento
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Validità (minuti)</Label>
                        <Input
                          type="number"
                          min={1}
                          max={10080}
                          value={ttlMinutes}
                          onChange={(e) => setTtlMinutes(parseInt(e.target.value) || 10)}
                        />
                        <p className="text-xs text-muted-foreground">
                          Max 7 giorni (10080 minuti)
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Link Monouso</Label>
                          <p className="text-xs text-muted-foreground">
                            Il link può essere usato una sola volta
                          </p>
                        </div>
                        <Switch checked={oneTime} onCheckedChange={setOneTime} />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Scope Accesso</Label>
                        <Select value={scope} onValueChange={setScope}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ADMIN_ONLY">Solo Admin</SelectItem>
                            <SelectItem value="RESIDENTS">Residenti</SelectItem>
                            <SelectItem value="UNIT_ONLY">Solo Unità Specifica</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Determina chi può accedere al link
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button onClick={createShareLink} disabled={creating}>
                        {creating ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
                        Crea Link
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {shareLinks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nessun link di condivisione creato
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Stato</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>TTL</TableHead>
                      <TableHead>Monouso</TableHead>
                      <TableHead>Utilizzi</TableHead>
                      <TableHead>Creato da</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shareLinks.map((link) => (
                      <TableRow key={link.id}>
                        <TableCell>{getStatusBadge(link.status)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {link.scope === 'ADMIN_ONLY' && <Shield className="h-3 w-3 mr-1" />}
                            {link.scope === 'RESIDENTS' && <User className="h-3 w-3 mr-1" />}
                            {link.scope === 'UNIT_ONLY' && <Globe className="h-3 w-3 mr-1" />}
                            {link.scope}
                          </Badge>
                        </TableCell>
                        <TableCell>{link.ttl_minutes} min</TableCell>
                        <TableCell>{link.one_time ? 'Sì' : 'No'}</TableCell>
                        <TableCell>{link.use_count || 0}</TableCell>
                        <TableCell className="text-sm">{link.created_by_email}</TableCell>
                        <TableCell className="text-sm">{formatDate(link.expires_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {link.status === 'ACTIVE' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => copyLink(`${API_URL}/api/share/${link.token}`)}
                                  title="Copia Link"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => revokeLink(link.token)}
                                  className="text-red-600"
                                  title="Revoca"
                                >
                                  <Ban className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Download Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Log Download</CardTitle>
                <CardDescription>
                  Audit trail completo di tutti gli accessi
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportLog('CSV')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportLog('PDF')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {downloadLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nessun download registrato
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Ora</TableHead>
                      <TableHead>Azione</TableHead>
                      <TableHead>Risultato</TableHead>
                      <TableHead>Utente</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Watermark</TableHead>
                      <TableHead>Share Link</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {downloadLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">{formatDate(log.timestamp)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {log.action?.replace('_DOWNLOAD', '')}
                          </Badge>
                        </TableCell>
                        <TableCell>{getResultBadge(log.result)}</TableCell>
                        <TableCell className="text-sm">
                          {log.actor_email || log.actor_name || '-'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {log.ip_address || '-'}
                        </TableCell>
                        <TableCell>
                          {log.watermark_applied ? (
                            <Badge className="bg-cyan-100 text-cyan-800 text-xs">Sì</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.share_token_id ? (
                            <Badge className="bg-purple-100 text-purple-800 text-xs">Sì</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
